import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";

const money = (n) =>
  `RD$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Panel visual de estado de crédito del cliente.
 * - Se muestra sólo cuando `customerId` está definido y el método de pago es "credito".
 * - Consulta `/api/customers/{id}/credit-status?amount={saleAmount}` y muestra:
 *   límite, pendiente, disponible, días vencidos, y mensajes de bloqueo si aplica.
 * - Devuelve `blocked` vía callback para que el POS pueda deshabilitar el checkout.
 */
export default function CustomerCreditPanel({ customerId, saleAmount = 0, onStatusChange, testid = "credit-status" }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!customerId) { setStatus(null); onStatusChange?.(null); return; }
    setLoading(true);
    api.get(`/customers/${customerId}/credit-status`, { params: { amount: saleAmount || 0 } })
      .then(({ data }) => {
        setStatus(data);
        onStatusChange?.(data);
      })
      .catch(() => { setStatus(null); onStatusChange?.(null); })
      .finally(() => setLoading(false));
  }, [customerId, saleAmount]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!customerId) return null;
  if (loading && !status) return <div className="text-xs text-muted-foreground p-2" data-testid={`${testid}-loading`}>Consultando estado de crédito…</div>;
  if (!status) return null;

  const evalRes = status.evaluation;
  const isBlocked = status.is_blocked || (evalRes && !evalRes.ok);
  const hasLimit = (status.credit_limit || 0) > 0;

  return (
    <div
      className={`p-3 rounded-md border text-sm ${isBlocked ? "border-destructive/60 bg-destructive/5" : "border-border bg-accent/30"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        {isBlocked ? (
          <XCircle className="w-4 h-4 text-destructive" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        )}
        <span className={`font-bold text-xs uppercase tracking-wide ${isBlocked ? "text-destructive" : "text-emerald-700 dark:text-emerald-400"}`}>
          {isBlocked ? "Crédito bloqueado" : "Crédito disponible"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">Límite:</span>
        <span className="data-cell font-bold text-right">
          {hasLimit ? money(status.credit_limit) : <span className="text-muted-foreground">Sin límite</span>}
        </span>

        <span className="text-muted-foreground">Pendiente:</span>
        <span className="data-cell text-right" data-testid={`${testid}-pending`}>{money(status.pending_balance)}</span>

        {hasLimit && (
          <>
            <span className="text-muted-foreground">Disponible:</span>
            <span className={`data-cell font-bold text-right ${(status.available_credit || 0) > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`} data-testid={`${testid}-available`}>
              {money(status.available_credit)}
            </span>
          </>
        )}

        <span className="text-muted-foreground">Facturas pendientes:</span>
        <span className="data-cell text-right">{status.invoices_pending_count}</span>

        {status.oldest_overdue_days > 0 && (
          <>
            <span className="text-muted-foreground">Vencimiento máximo:</span>
            <span
              className={`data-cell text-right font-bold ${status.oldest_overdue_days > status.max_overdue_days ? "text-destructive" : "text-amber-600 dark:text-amber-400"}`}
              data-testid={`${testid}-overdue-days`}
            >
              {status.oldest_overdue_days} días
              <span className="text-[10px] text-muted-foreground ml-1">/ tope {status.max_overdue_days}</span>
            </span>
          </>
        )}
      </div>

      {/* Mensajes de bloqueo */}
      {status.block_reasons && status.block_reasons.length > 0 && (
        <div className="mt-2 pt-2 border-t border-destructive/30 flex items-start gap-2" data-testid={`${testid}-block-msg`}>
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <div className="text-xs text-destructive">
            {status.block_reasons.map((r, i) => <div key={i}>{r}</div>)}
          </div>
        </div>
      )}

      {evalRes && !evalRes.ok && (
        <div className="mt-2 pt-2 border-t border-destructive/30 flex items-start gap-2" data-testid={`${testid}-limit-msg`}>
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <div className="text-xs text-destructive">{evalRes.reason}</div>
        </div>
      )}

      {status.oldest_overdue_days > 0 && status.oldest_overdue_days <= status.max_overdue_days && !isBlocked && (
        <div className="mt-2 pt-2 border-t border-amber-500/30 flex items-start gap-2 text-amber-700 dark:text-amber-400" data-testid={`${testid}-warn-msg`}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="text-xs">
            Este cliente tiene una factura vencida ({status.oldest_overdue_days} días).
            Se bloqueará el crédito si supera {status.max_overdue_days} días.
          </div>
        </div>
      )}
    </div>
  );
}
